import type React from 'react';
import {useCallback, useMemo} from 'react';
import {Box, Button} from '@mui/material';
import {Add} from '@mui/icons-material';
import type {GridColDef} from '@mui/x-data-grid-premium';
import {CustomDataGrid} from '~/components/data-grid/CustomDataGrid';
import {formatDate} from '~/pages/category-profile/domain/formatDate';
import {newId} from '../../../domain/planText';
import type {PlanAction} from '../../../types/strategyTypes';
import type {PlanProps} from '../strategyProps';
import {PlanSection} from '../PlanSection';
import {useOpenProfileTab} from '../../../hooks/useOpenProfileTab';
import {HelpTitle} from '../SectionLinks';

const ACTIONS_HELP = 'Five standard first actions, with deadlines counted from the day the plan was drafted (21 to 120 '
    + 'days) and the category manager as owner where known. Add, edit and remove them here; owners are picked from the '
    + 'category team. Stage 5 "Action planning" is met once every action has an owner and a deadline.';
import {RowAction} from '../../components/RowAction';

type Props = PlanProps & {
    /** Names to pick an owner from: the category team. */
    people: string[];
};

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Key actions and deadlines: every cell edits in place; Add action; Remove with Undo. */
export const ActionsSection: React.FC<Props> = ({plan, actions, people}) => {
    const openTab = useOpenProfileTab();
    const rows = plan.content.actions;
    const canEdit = plan.canEdit;
    const save = useCallback((next: PlanAction[], message: string) => void actions.save('actions', next, message), [actions]);

    const columns: GridColDef<PlanAction>[] = useMemo(() => [
        {field: 'action', headerName: 'Action', flex: 1, minWidth: 260, editable: canEdit},
        {field: 'owner', headerName: 'Owner', width: 200, type: 'singleSelect', editable: canEdit,
            valueOptions: ['', ...people], valueFormatter: (v: string) => v || '—'},
        {field: 'deadline', headerName: 'Deadline', width: 150, type: 'date', editable: canEdit,
            valueGetter: (v: string) => (v ? new Date(`${v}T00:00:00`) : null),
            valueFormatter: (v: Date | null) => (v ? formatDate(iso(v)) : '—')},
        ...(canEdit ? [{
            field: 'remove', headerName: '', width: 64, sortable: false, filterable: false, disableColumnMenu: true,
            align: 'center',
            renderCell: ({row}) => (
                <RowAction kind="delete" label={`action ${row.action}`} onClick={() => save(rows.filter(r => r.id !== row.id), 'Action removed.')}/>
            ),
        } satisfies GridColDef<PlanAction>] : []),
    ], [canEdit, people, rows, save]);

    return (
        <PlanSection title={<HelpTitle title="Key actions and deadlines" help={ACTIONS_HELP}
                                       links={[{label: 'the category team on Overview', onClick: () => openTab('overview')}]}/>} edited={plan.edited.includes('actions')}
                     actions={canEdit && (
                         <Button size="small" startIcon={<Add/>}
                                 onClick={() => save([...rows, {id: newId('a', rows), action: 'New action', owner: '', deadline: ''}], 'Action added.')}>
                             Add action
                         </Button>
                     )}>
            <Box sx={{height: Math.max(2, Math.min(rows.length, 10)) * 36 + 100}}>
                <CustomDataGrid
                    layoutKey="category-plan-actions"
                    rows={rows} columns={columns} getRowId={r => r.id}
                    initialState={{pagination: {paginationModel: {pageSize: 10, page: 0}}}}
                    disableRowSelectionOnClick
                    processRowUpdate={(next: Omit<PlanAction, 'deadline'> & {deadline: Date | string | null}, prev: PlanAction) => {
                        const deadline = next.deadline instanceof Date ? iso(next.deadline) : next.deadline ?? '';
                        const row = {...next, action: next.action.trim() || prev.action, deadline};
                        if (row.action !== prev.action || row.owner !== prev.owner || row.deadline !== prev.deadline) {
                            save(rows.map(r => (r.id === prev.id ? row : r)), 'Action updated.');
                        }
                        return row;
                    }}
                    localeText={{noRowsLabel: 'No actions yet.'}}
                />
            </Box>
        </PlanSection>
    );
};
